import React, { useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'

import noop from '@client/utils/noop'

const Droppable = ({ 
  children, 
  onDropEnter = noop,
  onDragOver = noop,
  onDragLeave = noop,
  onDrop = noop,
  onDragEnter = noop,
  canDrop = false 
}) => {
  const [domElement, setDomElement] = useState(null)
  const counterRef = useRef(0)

  // Validate children prop
  if (!children || !React.isValidElement(children)) {
    console.error('Droppable: children prop must be a valid React element')
    return null
  }

  const refCallback = useCallback((ref) => {
    if (!ref) {
      setDomElement(null)
      return
    }

    let element = null

    // Handle different ref types
    if (ref instanceof HTMLElement) {
      // Direct DOM element
      element = ref
    } else if (ref && typeof ref === 'object') {
      // Try common patterns for component refs
      if (ref.current instanceof HTMLElement) {
        // useRef pattern
        element = ref.current
      } else if (ref.getDOMElement && typeof ref.getDOMElement === 'function') {
        // useImperativeHandle pattern
        element = ref.getDOMElement()
      } else if (ref._reactInternals || ref._reactInternalInstance) {
        // Class component instance - try to find first DOM child
        const node = ref._reactInternals?.child?.stateNode || ref._reactInternalInstance?.child?.stateNode
        if (node instanceof HTMLElement) {
          element = node
        }
      }
    }

    setDomElement(element)
  }, [])

  const handleDrop = useCallback((event) => {
    event.preventDefault()

    if (canDrop) {
      onDrop(event, {
        el: domElement,
      })
    }

    counterRef.current = 0
  }, [canDrop, onDrop, domElement])

  const handleDragEnter = useCallback((event) => {
    event.preventDefault()
    counterRef.current++

    onDragEnter(event, {
      counter: counterRef.current,
      el: domElement,
    })
  }, [onDragEnter, domElement])

  const handleDragLeave = useCallback((event) => {
    event.preventDefault()
    counterRef.current--

    if (!counterRef.current) {
      onDragLeave(event, {
        el: domElement,
      })
    }
  }, [onDragLeave, domElement])

  const handleDragOver = useCallback((event) => {
    if (canDrop) {
      event.preventDefault()
    }
    onDragOver(event, {
      el: domElement,
    })
  }, [canDrop, onDragOver, domElement])

  useEffect(() => {
    if (!domElement) return

    // Add drag event listeners
    domElement.addEventListener('dragover', handleDragOver, false)
    domElement.addEventListener('dragleave', handleDragLeave, false)
    domElement.addEventListener('dragenter', handleDragEnter, false)
    domElement.addEventListener('drop', handleDrop, false)
    
    // Set position style
    domElement.style.position = 'relative'

    // Cleanup function
    return () => {
      if (domElement.removeEventListener) {
        domElement.removeEventListener('dragover', handleDragOver, false)
        domElement.removeEventListener('dragleave', handleDragLeave, false)
        domElement.removeEventListener('dragenter', handleDragEnter, false)
        domElement.removeEventListener('drop', handleDrop, false)
      }
    }
  }, [domElement, handleDragOver, handleDragLeave, handleDragEnter, handleDrop])

  return React.cloneElement(children, { ref: refCallback })
}

Droppable.propTypes = {
  onDropEnter: PropTypes.func,
  onDragOver: PropTypes.func,
  onDragLeave: PropTypes.func,
  onDrop: PropTypes.func,
  onDragEnter: PropTypes.func,
  children: PropTypes.element.isRequired,
  canDrop: PropTypes.bool,
}

export default Droppable
